import { useMemo, useState } from 'react';
import { Clock, ShoppingCart, Copy, Check } from 'lucide-react';
import type { FormaPagamento, ItemPedido, RestauranteCardapio } from '../../types/cardapio';
import { LABEL_CATEGORIA } from '../../types/cardapio';
import {
  estimarTempoMin,
  itensMenuAtivos,
  upsertPedido,
} from '../../utils/cardapio/persistencia';

const criarId = () => `ped-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

function formatarPreco(v: number) {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export default function CardapioCliente({
  restaurante,
  mesa,
}: {
  restaurante: RestauranteCardapio;
  mesa: number;
}) {
  const itens = useMemo(() => itensMenuAtivos(restaurante.slug), [restaurante.slug]);
  const [carrinho, setCarrinho] = useState<ItemPedido[]>([]);
  const [forma, setForma] = useState<FormaPagamento>('pix');
  const [pedidoId, setPedidoId] = useState<string | null>(null);
  const [pedidoTotal, setPedidoTotal] = useState(0);
  const [pixCopiado, setPixCopiado] = useState(false);
  const [tempoEst, setTempoEst] = useState<number | null>(null);

  const total = carrinho.reduce((s, i) => s + i.precoUnit * i.quantidade, 0);

  const adicionar = (itemId: string) => {
    const item = itens.find((i) => i.id === itemId);
    if (!item) return;
    setCarrinho((prev) => {
      const ex = prev.find((p) => p.itemId === itemId);
      if (ex) {
        return prev.map((p) =>
          p.itemId === itemId ? { ...p, quantidade: p.quantidade + 1 } : p
        );
      }
      return [
        ...prev,
        {
          itemId: item.id,
          nome: item.nome,
          quantidade: 1,
          precoUnit: item.preco,
        },
      ];
    });
  };

  const enviarPedido = () => {
    if (carrinho.length === 0) return;
    const agora = new Date().toISOString();
    const pedido = {
      id: criarId(),
      restauranteSlug: restaurante.slug,
      mesa,
      itens: carrinho,
      total,
      formaPagamento: forma,
      status:
        forma === 'dinheiro'
          ? ('aguardando_pagamento' as const)
          : forma === 'pix'
            ? ('aguardando_pagamento' as const)
            : ('aguardando_pagamento' as const),
      criadoEm: agora,
      atualizadoEm: agora,
    };
    const tempo = estimarTempoMin(restaurante.slug, pedido);
    pedido.tempoEstimadoMin = tempo;
    upsertPedido(restaurante.slug, pedido);
    setPedidoId(pedido.id);
    setPedidoTotal(total);
    setTempoEst(tempo);
    setCarrinho([]);
  };

  const porCategoria = useMemo(() => {
    const map = new Map<string, typeof itens>();
    itens.forEach((i) => {
      const cat = LABEL_CATEGORIA[i.categoria];
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(i);
    });
    return map;
  }, [itens]);

  if (pedidoId) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white p-4">
        <div className="max-w-md mx-auto ade-neu-card p-6 border border-orange-200 text-center">
          <h1 className="text-xl font-bold text-black">Pedido enviado!</h1>
          <p className="text-sm text-neutral-600 mt-1">
            Mesa {mesa} · {restaurante.nome}
          </p>
          <p className="text-2xl font-black text-orange-600 mt-3">#{pedidoId.slice(-6).toUpperCase()}</p>
          {forma === 'pix' && restaurante.chavePix && (
            <div className="mt-4 text-left bg-neutral-50 rounded-xl p-3 border">
              <p className="text-xs font-bold text-neutral-700">Pix — copia e cola</p>
              <p className="text-sm font-mono break-all mt-1">{restaurante.chavePix}</p>
              <p className="text-lg font-bold mt-2">{formatarPreco(pedidoTotal)}</p>
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard.writeText(restaurante.chavePix).then(() => {
                    setPixCopiado(true);
                    window.setTimeout(() => setPixCopiado(false), 2000);
                  });
                }}
                className="mt-2 w-full py-2 rounded-lg bg-[#009b3a] text-white text-sm font-semibold inline-flex items-center justify-center gap-1"
              >
                {pixCopiado ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                Copiar chave Pix
              </button>
            </div>
          )}
          {forma === 'dinheiro' && (
            <p className="mt-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Dirija-se ao balcão para pagar em dinheiro. O pedido só entra na fila após confirmação.
            </p>
          )}
          {tempoEst != null && (
            <p className="mt-4 text-sm flex items-center justify-center gap-1 text-neutral-700">
              <Clock className="w-4 h-4" />
              Tempo estimado: <strong>~{tempoEst} min</strong>
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white pb-28">
      <header className="bg-orange-600 text-white px-4 py-4 shadow">
        <h1 className="text-lg font-bold">{restaurante.nome}</h1>
        <p className="text-sm text-orange-100">Mesa {mesa}</p>
      </header>

      <main className="max-w-lg mx-auto p-4 space-y-4">
        {itens.length === 0 ? (
          <p className="text-center text-neutral-600 py-12 text-sm">
            Cardápio em configuração. Peça ao estabelecimento para cadastrar os itens.
          </p>
        ) : (
          Array.from(porCategoria.entries()).map(([cat, lista]) => (
            <section key={cat}>
              <h2 className="text-sm font-bold text-neutral-700 uppercase tracking-wide mb-2">
                {cat}
              </h2>
              <ul className="space-y-2">
                {lista.map((item) => (
                  <li
                    key={item.id}
                    className="bg-white rounded-xl border border-neutral-200 p-3 flex justify-between gap-2"
                  >
                    <div>
                      <p className="font-semibold text-black">{item.nome}</p>
                      {item.descricao && (
                        <p className="text-xs text-neutral-500">{item.descricao}</p>
                      )}
                      <p className="text-sm font-bold text-orange-600 mt-1">
                        {formatarPreco(item.preco)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => adicionar(item.id)}
                      className="shrink-0 self-center px-3 py-1.5 rounded-lg bg-orange-600 text-white text-xs font-bold"
                    >
                      +
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}
      </main>

      {carrinho.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold flex items-center gap-1">
              <ShoppingCart className="w-4 h-4" />
              {carrinho.reduce((s, i) => s + i.quantidade, 0)} item(ns)
            </span>
            <span className="font-bold text-orange-600">{formatarPreco(total)}</span>
          </div>
          <div className="flex gap-2 mb-2 text-[11px]">
            {(['pix', 'dinheiro'] as FormaPagamento[]).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setForma(f)}
                className={`flex-1 py-1.5 rounded-lg border font-semibold ${
                  forma === f ? 'bg-orange-100 border-orange-400' : 'border-neutral-200'
                }`}
              >
                {f === 'pix' ? 'Pix' : 'Dinheiro no balcão'}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={enviarPedido}
            className="w-full py-3 rounded-xl bg-orange-600 text-white font-bold"
          >
            Confirmar pedido
          </button>
        </div>
      )}
    </div>
  );
}
